import {
  getCurrentTimeZoneId,
  isoDateTimeToZonedDateTimeWithAnchorTime,
  formatDate,
  toAnchorTime
} from './date-utils'
import type { ISODateTimeString } from '@gat-ship/types'
import { ref } from 'vue'
import {
  isValidDay,
  isValidMinute,
  isValidHour,
  isValidYear,
  isValidMonth
} from '../utils/DateUtils'
import { Temporal } from '@js-temporal/polyfill'

function getSeparator(value: string) {
  // Try to detect separators from the input value
  const separators = ['.', '/', '-', ' ']
  let detectedSeparator: string | undefined

  for (const sep of separators) {
    if (value.includes(sep)) {
      detectedSeparator = sep
      break
    }
  }

  return detectedSeparator
}

function getFutureDateParts(day: number) {
  const now = Temporal.Now.zonedDateTimeISO()
  const result = {
    day: day as number | undefined,
    month: now.month as number | undefined,
    year: now.year as number | undefined,
    hour: now.hour as number | undefined,
    minute: now.minute as number | undefined
  }
  const future = now.add({ months: 1 })
  if (now.day > day) {
    return {
      day,
      month: future.month,
      year: future.year,
      hour: future.hour,
      minute: future.minute
    }
  }
  return {
    day,
    month: now.month,
    year: now.year,
    hour: now.hour,
    minute: now.minute
  }
}

function getYearFromString(year: string | undefined): number {
  const thisYear = Temporal.Now.zonedDateTimeISO().year
  if (!year) return thisYear
  if (year.length === 1) {
    // Single digit year - assume it's 2000s
    return 2000 + Number(year)
  } else if (year.length === 2) {
    // Two digit year - assume it's 2000s
    return 2000 + Number(year)
  }
  //   if (year.length > 4 || Number(year) >= 2100) {
  //     return thisYear
  //   }
  return Number(year)
}

function parseMinuteFromString(minutePart: string | undefined): number | undefined {
  if (!minutePart) {
    return undefined
  }
  const minute = Number(minutePart)
  if (isValidMinute(minute)) {
    return minute
  }
  return undefined
}

function parseHourFromString(hourPart: string | undefined): number | undefined {
  // The hour part can be 1 or 2 digits
  if (!hourPart) {
    return undefined
  }

  // Handle both 1 and 2 digit strings
  const hour = Number(hourPart)
  if (isValidHour(hour)) {
    return hour
  }

  return undefined
}

function validateDateParts(parts: {
  day: number | undefined
  month: number | undefined
  year: number | undefined
  hour: number | undefined
  minute: number | undefined
}) {
  const errorMessages: string[] = []
  console.log('Validation', parts.year)
  if (parts.year && !isValidYear(parts.year)) {
    errorMessages.push(`Invalid year: ${parts.year}`)
  }
  if (parts.month && !isValidMonth(parts.month)) {
    errorMessages.push(`Invalid month: ${parts.month}`)
  }
  if (parts.day && !isValidDay(parts.day, parts.month, parts.year)) {
    errorMessages.push(`Invalid day: ${parts.day}`)
  }
  if (parts.hour && !isValidHour(parts.hour)) {
    errorMessages.push(`Invalid hour: ${parts.hour}`)
  }
  if (parts.minute && !isValidMinute(parts.minute)) {
    errorMessages.push(`Invalid minute: ${parts.minute}`)
  }
  console.log('ERRORS:', errorMessages)
  return errorMessages
}

function getDatePartsFromInputValue(value: string, futureDate: boolean) {
  const result = {
    day: undefined as number | undefined,
    month: undefined as number | undefined,
    year: undefined as number | undefined,
    hour: undefined as number | undefined,
    minute: undefined as number | undefined
  }
  if (!value) return result

  // Get the separator. If no separator is found, we assume the input is a single number (day).
  const detectedSeparator = getSeparator(value)

  // If no separator is found, we assume the input is a single number (day).
  if (!detectedSeparator) {
    const day = Number(value)
    if (!isValidDay(day)) {
      return [`Invalid day: ${day}`]
    }
    if (futureDate) {
      return getFutureDateParts(day)
    }
    result.day = day
    // We don't need to validate the date parts because we already validated the day.
    return result
  }

  // We create a new value with the separator replaced by a hash.
  const newValue = value
    .replaceAll(detectedSeparator, '#')
    .replaceAll(' ', '#')
    .replaceAll(':', '#')

  // We split the input value into parts.
  const parts = newValue.split('#')

  const [day, month, year, hour, minute] = parts

  result.day = day ? Number(day) : undefined
  result.month = month ? Number(month) : undefined
  result.year = getYearFromString(year)
  result.hour = hour ? Number(hour) : undefined
  result.minute = minute ? Number(minute) : undefined

  const errorMessages = validateDateParts(result)

  if (errorMessages.length > 0) {
    return errorMessages
  }

  return result
}
/**
 * @param params - The date parts to format
 * @param timeZone - The time zone to use
 * @returns The formatted date with time.
 */
function parseDatePartsToISODateTimeString(
  params: {
    day?: number | string | undefined
    month?: number | string | undefined
    year?: number | string | undefined
    hour?: number | string | undefined
    minute?: number | string | undefined
    /** If anchorTime is set, the time will be set to midnight or noon in the given timeZone. */
    anchorTime?: 'midnight' | 'noon' | 'now'
  },
  timeZone: string
): ISODateTimeString {
  const year = getYearFromString(params.year?.toString())
  // Get the current time based on anchorTime setting
  const now = Temporal.Now.instant().toZonedDateTimeISO(timeZone)
  const dateWithTime = now.with({
    day: params.day !== undefined ? Number(params.day) : now.day,
    month: params.month !== undefined ? Number(params.month) : now.month,
    year,
    hour: params.hour !== undefined ? Number(params.hour) : now.hour,
    minute: params.minute !== undefined ? Number(params.minute) : now.minute,
    second: 0,
    millisecond: 0,
    microsecond: 0,
    nanosecond: 0
  })

  // Apply anchorTime if specified
  if (params.anchorTime) {
    return toAnchorTime(dateWithTime, params.anchorTime, timeZone)
      .toInstant()
      .toString() as ISODateTimeString
  }

  return dateWithTime.toInstant().toString() as ISODateTimeString
}

function inferInputValueToLocaleString(params: {
  value: string
  locale: string
  futureDate: boolean
}): {
  inputValue: string
  isValid: boolean
  errorMessages: string[]
  parts: {
    day: number | undefined
    month: number | undefined
    year: number | undefined
    hour: number | undefined
    minute: number | undefined
  }
} {
  if (!params.value)
    return {
      inputValue: params.value,
      isValid: false,
      errorMessages: [],
      parts: {
        day: undefined,
        month: undefined,
        year: undefined,
        hour: undefined,
        minute: undefined
      }
    }

  const dateParts = getDatePartsFromInputValue(params.value, params.futureDate)

  if (Array.isArray(dateParts)) {
    return {
      inputValue: params.value,
      isValid: false,
      errorMessages: dateParts,
      parts: {
        day: undefined,
        month: undefined,
        year: undefined,
        hour: undefined,
        minute: undefined
      }
    }
  }

  const now = Temporal.Now.instant().toZonedDateTimeISO(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  )

  const getMinute = () => {
    if (dateParts.minute !== undefined) {
      return dateParts.minute
    }
    if (dateParts.hour !== undefined) {
      return 0
    }
    return now.minute
  }
  const dateWithTime = now.with({
    day: dateParts.day !== undefined ? dateParts.day : now.day,
    month: dateParts.month !== undefined ? dateParts.month : now.month,
    year: dateParts.year !== undefined ? dateParts.year : now.year,
    hour: dateParts.hour !== undefined ? dateParts.hour : now.hour,
    minute: getMinute()
  })

  return {
    inputValue: formatDate(dateWithTime, params.locale, true),
    isValid: true,
    errorMessages: [],
    parts: dateParts
  }
}

export function useInputValue() {
  const inputValue = ref<string>('')

  function setInputValue(value: string) {
    inputValue.value = value
  }

  function getInputValueFromModel(
    model: ISODateTimeString,
    locale: string,
    options?: {
      anchorTime?: 'midnight' | 'noon' | 'now'
      timeZone?: string
    }
  ) {
    const inputValue = formatDate(
      isoDateTimeToZonedDateTimeWithAnchorTime(model, options),
      locale,
      true
    )
    return inputValue
  }

  function getUpdatedValuesFromUserInput({
    value,
    locale,
    futureDate,
    timeZone,
    anchorTime
  }: {
    value: string
    locale: string
    futureDate: boolean
    timeZone: string
    anchorTime?: 'midnight' | 'noon' | 'now'
  }) {
    const {
      inputValue: __inputValue,
      isValid,
      parts,
      errorMessages
    } = inferInputValueToLocaleString({
      value,
      locale,
      futureDate
    })
    if (!isValid) {
      return {
        inputValue: value,
        localDateTime: '',
        timezoneDateTime: '',
        model: undefined,
        errorMessages
      }
    }
    // console.table(parts)

    const model = parseDatePartsToISODateTimeString(parts, getCurrentTimeZoneId())

    return {
      inputValue: __inputValue,
      model,
      errorMessages: []
    }
  }

  return { inputValue, setInputValue, getInputValueFromModel, getUpdatedValuesFromUserInput }
}
